/**
 * Money rendering for the sponsor surfaces (ui#496). Plain TypeScript — this module imports NO React —
 * because "what does 24900 minor RON look like to a Romanian reader" has one exact expected answer, and
 * an answer that exact belongs somewhere a test can pin it rather than inside a component.
 *
 * The api speaks only in MINOR units (`dailyRateMinor`, `amountMinor`) and a currency code, which is the
 * right wire format — it is integer arithmetic all the way from Stripe — but it is unreadable, so the
 * conversion happens once, here, instead of at each of the four places that show a price.
 */
import {minorToMajor} from "../core/format/minorUnits";
import {numberFormat} from "../core/format/intlFormats";

/**
 * Formats a minor-unit amount as currency for the given locale (`"en"` / `"ro"` — whatever
 * `useLocale()` reports; typed as a plain string so this module owes nothing to the React i18n layer).
 *
 * The minor-unit exponent comes from {@link ../core/format/minorUnits}, which reads it back from ICU
 * rather than assuming 2 — see that module for why a hardcoded `/ 100` is the bug worth preventing.
 * This is the SPONSOR surface's presentation of money (ICU currency style: "12,50 RON"); the pricing
 * page renders the same kind of amount through its own glyph map ("14,99 lei") — one shared
 * conversion, two deliberate presentations.
 *
 * Unlike the glyph-map path, this one THROWS on a currency code ICU cannot resolve, because it builds a
 * real ICU currency formatter. That is intentional: a sponsor amount with no resolvable currency has no
 * correct rendering, and inventing one would be worse than failing.
 */
export function formatMinor(amountMinor: number, currency: string, locale: string): string {
	// The api sends Stripe's lowercase codes ("ron"). No normalisation is needed: currency codes are
	// matched case-insensitively by Intl, which is asserted in the tests rather than assumed.
	return numberFormat(locale, {style: "currency", currency}).format(minorToMajor(amountMinor, currency));
}
